import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  TextField
} from '@mui/material';
import { V4_DIALOG_CONTENT } from '../../../../theme/v4Compat';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { extractApiErrorMessage, notifications } from '../../../../utils';
import { ApiService } from '../../../../hooks';
import { HealthSystemAPIControllersTaskV1ResponsesProductionScheduleResponse } from '../../../../api/HealthSystemApi';

interface Props {
  open: boolean;
  currentSchedule: HealthSystemAPIControllersTaskV1ResponsesProductionScheduleResponse | null;
  onClose: () => void;
  onCreated: () => void;
}

const CreateProductionScheduleModal: React.FC<Props> = ({ open, currentSchedule, onClose, onCreated }) => {
  const { t } = useTranslation();
  const apiService = new ApiService();

  const [totalWeeks, setTotalWeeks] = React.useState<number | ''>('');
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [totalWeeksError, setTotalWeeksError] = React.useState(false);
  const [startDateError, setStartDateError] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setTotalWeeks('');
      setStartDate(null);
      setTotalWeeksError(false);
      setStartDateError(false);
    }
  }, [open]);

  const validate = (): boolean => {
    let valid = true;

    if (!totalWeeks || (totalWeeks as number) < 1) {
      setTotalWeeksError(true);
      valid = false;
    } else {
      setTotalWeeksError(false);
    }

    if (!startDate) {
      setStartDateError(true);
      valid = false;
    } else {
      setStartDateError(false);
    }

    return valid;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    setIsBusy(true);
    apiService.api.taskList
      .taskListCreateProductionSchedule({
        totalWeeks: totalWeeks as number,
        startDate: format(startDate!, 'yyyy-MM-dd'),
        dismissCurrentSchedule: true
      })
      .then(() => {
        notifications.customInfo(t('f-tasks-production-schedule-create-success'), 200);
        onCreated();
        onClose();
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="create-production-schedule-title">
      <DialogTitle id="create-production-schedule-title">{t('f-tasks-production-schedule-create')}</DialogTitle>

      <DialogContent sx={V4_DIALOG_CONTENT}>
        <div className="flex flex-col gap-4 mt-2">
          {currentSchedule && <Alert severity="warning">{t('f-tasks-production-schedule-confirm-text')}</Alert>}

          <TextField
            label={t('f-tasks-production-schedule-total-weeks')}
            type="number"
            size="small"
            fullWidth
            variant="outlined"
            value={totalWeeks}
            onChange={(e) => setTotalWeeks(e.target.value === '' ? '' : Number(e.target.value))}
            slotProps={{ htmlInput: { min: 1, max: 52 } }}
            error={totalWeeksError}
            helperText={totalWeeksError ? t('f-tasks-production-schedule-total-weeks') + ' is required (min 1)' : ''}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
              format="dd/MM/yyyy"
              label={t('f-tasks-production-schedule-start-date')}
              value={startDate}
              onChange={(value) => setStartDate(value as Date | null)}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  variant: 'outlined',
                  error: startDateError,
                  helperText: startDateError ? t('f-tasks-production-schedule-start-date') + ' is required' : ''
                }
              }}
            />
          </LocalizationProvider>
        </div>
      </DialogContent>

      <DialogActions>
        {isBusy && <CircularProgress size="1.5rem" />}

        <Button onClick={onClose} variant="outlined" startIcon={<Icon>{'close'}</Icon>} color="primary">
          {t('f-com-cancel')}
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={isBusy}
          startIcon={<Icon>{'check_circle_outline'}</Icon>}
          variant="contained"
          color="primary"
        >
          {t('f-com-create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProductionScheduleModal;
